import React from 'react'
import { Currency, Price } from '@uniswap/sdk-core'
import StepCounter from 'components/InputStepCounter/InputStepCounter'
import { RowBetween } from 'components/Row'
import { useActiveWeb3React } from 'hooks'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import { SmallButton } from 'pages/AddLiquidity/styled'
import { formatPriceString } from 'utils/formatTokenAmount'

// currencyA is the base token
export default function RangeSelector({
  priceLower,
  priceUpper,
  onLeftRangeInput,
  onRightRangeInput,
  getDecrementLower,
  getIncrementLower,
  getDecrementUpper,
  getIncrementUpper,
  currencyA,
  currencyB,
  feeAmount,
}: {
  priceLower?: Price
  priceUpper?: Price
  getDecrementLower: () => string
  getIncrementLower: () => string
  getDecrementUpper: () => string
  getIncrementUpper: () => string
  onLeftRangeInput: (typedValue: string) => void
  onRightRangeInput: (typedValue: string) => void
  currencyA?: Currency | null
  currencyB?: Currency | null
  feeAmount?: number
}) {
  const { chainId } = useActiveWeb3React()
  const tokenA = wrappedCurrency(currencyA ?? undefined, chainId)
  const tokenB = wrappedCurrency(currencyB ?? undefined, chainId)
  const isSorted = tokenA && tokenB && tokenA?.address !== tokenB?.address && tokenA.sortsBefore(tokenB)

  const leftPrice = isSorted ? priceLower : priceUpper?.invert()
  const rightPrice = isSorted ? priceUpper : priceLower?.invert()

  const tokenADecimals = tokenA?.decimals
  const tokenBDecimals = tokenB?.decimals

  const handleInfiniteRange = () => {
    if (tokenADecimals === 8 || tokenBDecimals === 8) {
      onLeftRangeInput('0.00009998')
    } else {
      onLeftRangeInput('0.000000000029263')
    }

    if (tokenADecimals === 8 || tokenBDecimals === 8) {
      onRightRangeInput('33849000000000000000000000000')
    } else {
      onRightRangeInput('340182366920938463463374607431768211456')
    }
  }

  return (
    <>
      <RowBetween>
        <SmallButton
          active={
            formatPriceString(leftPrice?.toSignificant(5)) === '≈0' &&
            formatPriceString(rightPrice?.toSignificant(5)) === '∞'
          }
          onClick={() => {
            handleInfiniteRange()
          }}
        >
          ∞ Price Range
        </SmallButton>
        <SmallButton
          onClick={() => {
            onLeftRangeInput('')
            onRightRangeInput('')
          }}
        >
          Reset Price Range
        </SmallButton>
      </RowBetween>
      <RowBetween>
        <StepCounter
          value={leftPrice?.toSignificant(5) ?? ''}
          onUserInput={onLeftRangeInput}
          width="48%"
          decrement={isSorted ? getDecrementLower : getIncrementUpper}
          increment={isSorted ? getIncrementLower : getDecrementUpper}
          feeAmount={feeAmount}
          label={leftPrice ? `${currencyB?.symbol}` : '-'}
          title={'Min Price'}
          tokenA={currencyA?.symbol}
          tokenB={currencyB?.symbol}
        />
        <StepCounter
          value={rightPrice?.toSignificant(5) ?? ''}
          onUserInput={onRightRangeInput}
          width="48%"
          decrement={isSorted ? getDecrementUpper : getIncrementLower}
          increment={isSorted ? getIncrementUpper : getDecrementLower}
          feeAmount={feeAmount}
          label={rightPrice ? `${currencyB?.symbol}` : '-'}
          tokenA={currencyA?.symbol}
          tokenB={currencyB?.symbol}
          title={'Max Price'}
        />
      </RowBetween>
    </>
  )
}
